/**
 * As etiquetas da equipe na tela (13.6, chamado de 21/10/2026, P5; FR-86, FR-62/PN4).
 *
 * As três etiquetas automáticas da UX-DR7 saíram: a tela mostra o que a **equipe** marca. Aqui moram as quatro peças que as
 * telas usam:
 *
 * - `ChipDeEtiqueta` / `ChipsDeEtiquetas` — o chip pastel com o nome por extenso (cor nunca é o
 *   único sinal). A cor é o **nome** da paleta, e o CSS a traduz em `--pastel-<cor>`
 *   (`tokens.css`). Os chips leem o catálogo (`useEtiquetas`, uma consulta para a tela inteira):
 *   renomear ou trocar a cor vale em todo lugar na próxima leitura. Um id que o catálogo ainda não
 *   tem não desenha nada; - `EtiquetasDoAlvo` — os chips de uma Empresa ou Pessoa com "Retirar" e o
 *   seletor "Aplicar etiqueta" (com "Criar etiqueta…"), gravando na hora. Só com `editar_empresa` —
 *   conveniência, e não autorização: quem recusa é o serviço (`403`, ou `404` pela RLS do alvo).
 *   **No modo compacto** (13.15, a linha da lista de Pessoas), até dois chips numa linha só, com
 *   "+N", e o botão "+ Etiqueta" sempre no mesmo lugar, que abre aplicar, retirar e criar num
 *   painel em portal (`document.body`, `position: fixed`): fora de todo `overflow` da tabela e do
 *   cartão;
 * - `GerenciadorDeEtiquetas` — criar, renomear, trocar a cor e apagar, aberto a **todo** usuário pelo
 *   botão "Etiquetas" das telas Empresas e Pessoas (D-6: no topo da lista, e não num modal). Apagar
 *   em uso mostra a mensagem do servidor **verbatim** e, conforme os números do `409` (`erro.uso`),
 *   "Ver empresas" e "Ver pessoas", que levam às listas filtradas com a contagem no *state*;
 * - `FiltroPorEtiqueta` — o seletor da barra das duas listas.
 *
 * **O aviso das escondidas** (D-11, `useAvisoDasEscondidas`): quem chega pelo aviso de apagar já
 * recebeu o total (a exceção de quantidade do FR-62); a lista, pós-RLS, compara uma vez com o total
 * que ela enxerga e diz quantas estão escondidas. Fora disso a lista não diz nada — mostrar o total
 * nela seria uma segunda porta para a contagem.
 *
 * **Os dois catálogos (13.18, chamado de 26/10, G).** A etiqueta é de Empresa **ou** de Pessoa, e
 * cada peça recebe o tipo: Empresas, a ficha e a ficha nova usam o catálogo `empresa`; Pessoas, o
 * painel compacto e Dados do contato, o `pessoa`. "VIP" de Empresa e "VIP" de Pessoa são duas. O
 * aviso de apagar em uso tem um número só e um caminho só ("Ver empresas" **ou** "Ver pessoas").
 * `EscolhaDeEtiquetas` é a escolha da ficha nova, que ainda não tem a quem aplicar: ela só junta os
 * ids, e a Empresa nasce com eles.
 */

import { Link, useRouter, useRouterState } from '@tanstack/react-router'
import {
  type CSSProperties,
  type KeyboardEvent,
  type Ref,
  type RefObject,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

import { ErroDaApi, MENSAGEM_DE_ERRO_DESCONHECIDO } from '@/api/erros'
import { CORES_PASTEL, NOMES_DAS_CORES } from '@/modulos/negocios/api'
import { CAMINHOS } from '@/shell/navegacao'
import {
  LIMITE_DE_ETIQUETAS_NA_FICHA_NOVA,
  TAMANHO_DO_NOME_DA_ETIQUETA,
  type AlvoDaEtiqueta,
  type CorDaEtiqueta,
  type Etiqueta,
  type TipoDeEtiqueta,
  type UsoDaEtiqueta,
  useAplicarEtiqueta,
  useApagarEtiqueta,
  useCriarEtiqueta,
  useEditarEtiqueta,
  useEtiquetas,
  useRetirarEtiqueta,
  usoDaEtiquetaEmUso,
} from './api'
import './etiquetas.css'

/* --- Os textos de tela (com acento; a mensagem do servidor vai verbatim) --------------------- */

export const ROTULO_DO_BOTAO_DE_ETIQUETAS = 'Etiquetas'
export const NOME_DO_GERENCIADOR = 'Etiquetas da equipe'
export const CRIAR_ETIQUETA = 'Criar etiqueta'
export const CRIAR_NO_SELETOR = 'Criar etiqueta…'
export const CRIAR_E_APLICAR = 'Criar e aplicar'
export const APAGAR_A_ETIQUETA = 'Apagar a etiqueta'
export const ETIQUETA_CRIADA = 'Etiqueta criada.'
export const ETIQUETA_SALVA = 'Etiqueta salva.'
export const ETIQUETA_APAGADA = 'Etiqueta apagada.'
export const VER_EMPRESAS = 'Ver empresas'
export const VER_PESSOAS = 'Ver pessoas'
export const FILTRAR_POR_ETIQUETA = 'Filtrar por etiqueta'
export const TODAS_AS_ETIQUETAS = 'Todas as etiquetas'
export const ETIQUETA_REMOVIDA = 'Etiqueta removida'
export const LIMPAR_FILTRO = 'Limpar filtro'
export const SEM_ETIQUETAS = 'Nenhuma etiqueta criada ainda.'
export const APLICAR_ETIQUETA = 'Aplicar etiqueta'
/** O botão do modo compacto (13.15): abre o painel de aplicar, retirar e criar. */
export const MAIS_ETIQUETA = '+ Etiqueta'
/** Quantos chips a linha compacta mostra antes do "+N" (13.15, H). */
export const CHIPS_NA_LINHA = 2

/** O valor da opção "Criar etiqueta…" do seletor de aplicar — nunca um uuid. */
export const VALOR_DE_CRIAR = '__criar__'

export const retirarAEtiqueta = (nome: string) => `Retirar a etiqueta ${nome}`
export const aplicarEtiquetaA = (alvo: string) => `Aplicar etiqueta a ${alvo}`
export const etiquetasDe = (alvo: string) => `Etiquetas de ${alvo}`
/** O nome acessível do "+ Etiqueta": o texto visível, e de quem é a linha. */
export const maisEtiquetaPara = (alvo: string) => `${MAIS_ETIQUETA} para ${alvo}`

/**
 * "Há 2 empresas com essa etiqueta que você não enxerga. Peça à gestão para retirá-la." — com
 * singular e plural. A gestão enxerga todas, e é ela quem limpa o que a pessoa não vê.
 */
export function avisoDasEscondidas(tipo: 'empresas' | 'pessoas', quantas: number): string {
  const singular = tipo === 'empresas' ? 'empresa' : 'pessoa'
  const nome = quantas === 1 ? singular : tipo
  return (
    `Há ${String(quantas)} ${nome} com essa etiqueta que você não enxerga. ` +
    'Peça à gestão para retirá-la.'
  )
}

/*
 * O *state* do histórico que "Ver empresas"/"Ver pessoas" levam (D-11): a contagem do `409`, presa à
 * etiqueta dela — o número de outra etiqueta não vale para esta.
 */
declare module '@tanstack/history' {
  interface HistoryState {
    usoDaEtiqueta?: {
      etiquetaId: string
      /** O catálogo da etiqueta (13.18): é ele que diz qual lista compara. */
      tipo: TipoDeEtiqueta
      quantidade: number
      /**
       * Quantas a lista contou escondidas, **uma vez** — gravado no próprio *state* (e não no
       * componente) para sobreviver ao "voltar" da ficha e ao recarregar: retirar as visíveis e
       * voltar não pode recontar sobre um total menor.
       */
      escondidas?: number
    }
  }
}

function mensagemDe(falha: unknown): string {
  return falha instanceof ErroDaApi ? falha.message : MENSAGEM_DE_ERRO_DESCONHECIDO
}

/** `id -> etiqueta` do catálogo do tipo carregado (vazio enquanto ele não chega). */
export function useCatalogoDeEtiquetas(tipo: TipoDeEtiqueta): ReadonlyMap<string, Etiqueta> {
  const consulta = useEtiquetas(tipo)
  return useMemo(
    () => new Map((consulta.data ?? []).map((etiqueta) => [etiqueta.id, etiqueta])),
    [consulta.data],
  )
}

/** As etiquetas dos ids, **na ordem dada** e sem repetir, sem os que o catálogo ainda não tem. */
export function resolverNaOrdemDada(
  ids: readonly string[],
  catalogo: ReadonlyMap<string, Etiqueta>,
): Etiqueta[] {
  return [...new Set(ids)].flatMap((id) => {
    const etiqueta = catalogo.get(id)
    return etiqueta === undefined ? [] : [etiqueta]
  })
}

/** As etiquetas dos ids, **na ordem do catálogo**, sem os que ele ainda não tem. */
function resolver(
  ids: readonly string[],
  catalogo: ReadonlyMap<string, Etiqueta>,
  ordem: readonly Etiqueta[],
): Etiqueta[] {
  const pedidas = new Set(ids)
  return ordem.filter((etiqueta) => pedidas.has(etiqueta.id) && catalogo.has(etiqueta.id))
}

/* --- O chip ----------------------------------------------------------------------------------- */

export function ChipDeEtiqueta({ etiqueta }: { etiqueta: Etiqueta }) {
  return (
    <span className="etiqueta" data-cor={etiqueta.cor} title={etiqueta.nome}>
      {etiqueta.nome}
    </span>
  )
}

/** Os chips, e o "+N" dos que passam do `maximo` — com os nomes no nome acessível e no `title`. */
function ListaDeChips({ etiquetas, maximo }: { etiquetas: readonly Etiqueta[]; maximo?: number }) {
  if (etiquetas.length === 0) {
    return null
  }
  const visiveis = maximo === undefined ? etiquetas : etiquetas.slice(0, maximo)
  const escondidas = etiquetas.slice(visiveis.length)
  const nomesEscondidos = escondidas.map((etiqueta) => etiqueta.nome).join(', ')
  return (
    <span className="etiquetas">
      {visiveis.map((etiqueta) => (
        <ChipDeEtiqueta key={etiqueta.id} etiqueta={etiqueta} />
      ))}
      {escondidas.length === 0 ? null : (
        <span
          className="etiqueta etiqueta--mais"
          role="img"
          aria-label={`Mais ${String(escondidas.length)}: ${nomesEscondidos}`}
          title={nomesEscondidos}
        >
          {`+${String(escondidas.length)}`}
        </span>
      )}
    </span>
  )
}

/**
 * Os chips de uma lista de ids **de um catálogo** (13.18), na ordem dada — a da leitura (o serviço já
 * devolve na ordem do catálogo). Com `maximo`, os que passam viram "+N".
 */
export function ChipsDeEtiquetas({
  tipo,
  ids,
  maximo,
}: {
  tipo: TipoDeEtiqueta
  ids: readonly string[]
  maximo?: number
}) {
  const catalogo = useCatalogoDeEtiquetas(tipo)
  return <ListaDeChips etiquetas={resolverNaOrdemDada(ids, catalogo)} maximo={maximo} />
}

/**
 * Os chips da conversa do WhatsApp (13.18): as da Pessoa, do catálogo `pessoa`, e depois as da
 * Empresa, do catálogo `empresa` — duas listas, sem juntar por id: "VIP" de Pessoa e "VIP" de
 * Empresa saem as duas. O `maximo` é dividido na ordem (primeiro as da Pessoa), e o "+N" conta as
 * duas.
 */
export function ChipsDaConversa({
  daPessoa,
  daEmpresa,
  maximo,
}: {
  daPessoa: readonly string[]
  daEmpresa: readonly string[]
  maximo?: number
}) {
  const deContatos = useCatalogoDeEtiquetas('pessoa')
  const deEmpresas = useCatalogoDeEtiquetas('empresa')
  const etiquetas = [
    ...resolverNaOrdemDada(daPessoa, deContatos),
    ...resolverNaOrdemDada(daEmpresa, deEmpresas),
  ]
  return <ListaDeChips etiquetas={etiquetas} maximo={maximo} />
}

/* --- A cor ------------------------------------------------------------------------------------ */

function SeletorDaCor({
  id,
  rotulo,
  valor,
  aoTrocar,
  desabilitado = false,
}: {
  id: string
  rotulo: string
  valor: CorDaEtiqueta
  aoTrocar: (cor: CorDaEtiqueta) => void
  desabilitado?: boolean
}) {
  return (
    <label className="etiquetas__campo" htmlFor={id}>
      <span className="etiquetas__rotulo">{rotulo}</span>
      <select
        id={id}
        className="etiquetas__selecao"
        value={valor}
        disabled={desabilitado}
        onChange={(evento) => {
          aoTrocar(evento.target.value as CorDaEtiqueta)
        }}
      >
        {CORES_PASTEL.map((cor) => (
          <option key={cor} value={cor}>
            {NOMES_DAS_CORES[cor]}
          </option>
        ))}
      </select>
    </label>
  )
}

/** `Enter` no campo do nome cria — e não envia o formulário em volta (a ficha da Empresa). */
function aoApertar(acao: () => void) {
  return (evento: KeyboardEvent<HTMLInputElement>) => {
    if (evento.key === 'Enter') {
      evento.preventDefault()
      acao()
    }
  }
}

/* --- Aplicar e retirar ------------------------------------------------------------------------ */

export interface PropsDasEtiquetasDoAlvo {
  alvo: AlvoDaEtiqueta
  /** O nome de quem recebe — entra nos nomes acessíveis ("Aplicar etiqueta a Marina"). */
  nomeDoAlvo: string
  etiquetaIds: readonly string[]
  /** `editar_empresa` na sessão: conveniência, e não autorização. */
  podeEditar: boolean
  /**
   * O modo compacto da linha da lista (13.15, chamado de 26/10, H): até `CHIPS_NA_LINHA` chips numa
   * linha só, com "+N", e o "+ Etiqueta", que abre o resto num painel em portal.
   */
  compacto?: boolean
  /**
   * O painel estava aberto quando a linha saiu da árvore (a etiqueta do filtro foi retirada, e a
   * lista relida não a traz mais): o foco sumiria com ela, e quem monta o leva a outro lugar.
   */
  aoSumirAberto?: () => void
}

/** O que recebe foco por `Tab` dentro do painel (o seletor do `FOCAVEIS` do modal da Empresa). */
const FOCAVEIS_DO_PAINEL =
  'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), ' +
  'textarea:not(:disabled), [tabindex]:not([tabindex="-1"])'

/** O que, focado pelo clique fora, fica com o foco quando o painel fecha. */
const CONTROLES_QUE_GUARDAM_O_FOCO =
  'input, select, textarea, button, a[href], [contenteditable="true"]'

/** A folga entre o botão e o painel, e entre o painel e a borda da janela (px). */
export const FOLGA_DO_PAINEL = 4
export const MARGEM_DO_PAINEL = 8

/** A parte da página que se vê, nas coordenadas do `position: fixed`. */
export interface JanelaVisivel {
  x: number
  y: number
  largura: number
  altura: number
}

/** O retângulo do botão, nas mesmas coordenadas (`getBoundingClientRect`). */
export type CaixaDoBotao = Pick<DOMRect, 'top' | 'bottom' | 'left' | 'right'>

/**
 * A janela que se vê: a `visualViewport` quando o navegador a tem (o teclado do celular e o zoom de
 * pinça encolhem a área visível sem mudar o `innerHeight`), e a janela inteira sem ela.
 */
export function janelaVisivel(): JanelaVisivel {
  const visual = window.visualViewport
  if (visual !== null && visual !== undefined) {
    return {
      x: visual.offsetLeft,
      y: visual.offsetTop,
      largura: visual.width,
      altura: visual.height,
    }
  }
  return {
    x: 0,
    y: 0,
    largura: document.documentElement.clientWidth || window.innerWidth,
    altura: window.innerHeight,
  }
}

/**
 * Onde o painel fica, em `position: fixed`: abaixo do botão, alinhado à direita dele, e acima
 * quando embaixo não cabe (a última linha da lista). Nunca fora da janela: encosta na margem
 * lateral e, sem lugar nem em cima, encosta na margem de baixo.
 */
export function posicaoDoPainel(
  botao: CaixaDoBotao,
  largura: number,
  altura: number,
  janela: JanelaVisivel,
): { top: number; left: number } {
  const esquerdaMinima = janela.x + MARGEM_DO_PAINEL
  const esquerdaMaxima = janela.x + janela.largura - largura - MARGEM_DO_PAINEL
  const esquerda = Math.max(esquerdaMinima, Math.min(botao.right - largura, esquerdaMaxima))
  const topoMinimo = janela.y + MARGEM_DO_PAINEL
  const baseMaxima = janela.y + janela.altura - MARGEM_DO_PAINEL
  const embaixo = botao.bottom + FOLGA_DO_PAINEL
  const emCima = botao.top - FOLGA_DO_PAINEL - altura
  const topo =
    embaixo + altura <= baseMaxima
      ? embaixo
      : emCima >= topoMinimo
        ? emCima
        : Math.max(topoMinimo, baseMaxima - altura)
  return { top: topo, left: esquerda }
}

/** O botão saiu da parte da página que se vê (a página rolou, a janela encolheu). */
export function botaoForaDaJanela(botao: CaixaDoBotao, janela: JanelaVisivel): boolean {
  return (
    botao.bottom < janela.y ||
    botao.top > janela.y + janela.altura ||
    botao.right < janela.x ||
    botao.left > janela.x + janela.largura
  )
}

/** O `min-width` calculado do painel, em px — `0` sem ele (ou no jsdom, que não calcula `min()`). */
function larguraMinimaDe(caixa: HTMLElement): number {
  const minima = Number.parseFloat(getComputedStyle(caixa).minWidth)
  return Number.isFinite(minima) ? minima : 0
}

/**
 * Onde o painel em portal mora (code review da 13.19c1): o diálogo que contém a âncora, quando há
 * um (o modal do "+ Empresa", o do Negócio) — o leitor de tela trata o fora do `aria-modal` como
 * inerte, e o "clique fora" do modal contaria a lista como fundo —, e o `document.body` fora dele.
 * A `position: fixed` e a camada `--camada-painel-flutuante` valem nos dois.
 */
export function conteinerDoPainel(ancora: Element | null): HTMLElement {
  return ancora?.closest<HTMLElement>('[role="dialog"]') ?? document.body
}

/**
 * A posição de um painel em portal (`position: fixed`, no `document.body`) presa a uma âncora —
 * a peça da 13.15 que tira o painel de todo `overflow` de tabela e de cartão, reusada também pela
 * lista do campo "Grupo" da ficha nova (13.18).
 *
 * `null` enquanto fechado ou ainda não medido (quem monta desenha o painel `visibility: hidden` até
 * lá). Aberto, acompanha a âncora: ao abrir, quando o conteúdo muda de tamanho, e quando a página
 * rola ou a janela muda. Se a âncora sai da parte visível, chama `aoSairDaJanela` (o painel das
 * etiquetas fecha). Com `daLarguraDaAncora`, o painel tem a largura da âncora (a lista suspensa de
 * um campo).
 */
export function usePosicaoDoPainel(
  aberto: boolean,
  ancora: RefObject<HTMLElement | null>,
  painel: RefObject<HTMLElement | null>,
  {
    aoSairDaJanela,
    daLarguraDaAncora = false,
  }: { aoSairDaJanela?: () => void; daLarguraDaAncora?: boolean } = {},
): CSSProperties | null {
  const [posicao, setPosicao] = useState<CSSProperties | null>(null)
  if (!aberto && posicao !== null) {
    setPosicao(null)
  }
  const aoSair = useRef(aoSairDaJanela)
  useEffect(() => {
    aoSair.current = aoSairDaJanela
  })

  useLayoutEffect(() => {
    if (!aberto) {
      return
    }
    const posicionar = (conferirAAncora: boolean) => {
      const daAncora = ancora.current?.getBoundingClientRect()
      const caixa = painel.current
      if (daAncora === undefined || caixa === null) {
        return
      }
      const janela = janelaVisivel()
      if (conferirAAncora && botaoForaDaJanela(daAncora, janela)) {
        aoSair.current?.()
        return
      }
      /* Com a largura da âncora, mas nunca menor que a mínima do próprio painel (a sugestão de
         Pessoas tem `min-width`): a posição é calculada com a largura que ele terá de fato, e a
         borda direita não sai da janela (code review da 13.19c1). */
      const largura = daLarguraDaAncora
        ? Math.max(daAncora.width, larguraMinimaDe(caixa))
        : caixa.offsetWidth
      /* A lista de um campo começa na borda esquerda dele (e cresce para a direita quando é mais
         larga que ele); o painel de um botão termina na borda direita do botão. */
      const referencia = daLarguraDaAncora
        ? {
            top: daAncora.top,
            bottom: daAncora.bottom,
            left: daAncora.left,
            right: daAncora.left + largura,
          }
        : daAncora
      const lugar = posicaoDoPainel(referencia, largura, caixa.offsetHeight, janela)
      setPosicao(daLarguraDaAncora ? { ...lugar, width: largura } : lugar)
    }
    const aoMover = () => {
      posicionar(true)
    }
    posicionar(false)
    const visual = window.visualViewport
    window.addEventListener('resize', aoMover)
    window.addEventListener('scroll', aoMover, true)
    visual?.addEventListener('resize', aoMover)
    visual?.addEventListener('scroll', aoMover)
    // O `ResizeObserver` não existe no jsdom: lá o painel fica onde abriu.
    const observador =
      typeof ResizeObserver === 'undefined'
        ? undefined
        : new ResizeObserver(() => {
            posicionar(false)
          })
    if (painel.current !== null) {
      observador?.observe(painel.current)
    }
    return () => {
      window.removeEventListener('resize', aoMover)
      window.removeEventListener('scroll', aoMover, true)
      visual?.removeEventListener('resize', aoMover)
      visual?.removeEventListener('scroll', aoMover)
      observador?.disconnect()
    }
  }, [aberto, ancora, painel, daLarguraDaAncora])

  return aberto ? posicao : null
}

export function EtiquetasDoAlvo({
  alvo,
  nomeDoAlvo,
  etiquetaIds,
  podeEditar,
  compacto = false,
  aoSumirAberto,
}: PropsDasEtiquetasDoAlvo) {
  /* 13.18: o catálogo é o do tipo do alvo — a Empresa só vê as de Empresa, a Pessoa as de Pessoa. */
  const consulta = useEtiquetas(alvo.tipo)
  const catalogo = useCatalogoDeEtiquetas(alvo.tipo)
  const aplicar = useAplicarEtiqueta(alvo)
  const retirar = useRetirarEtiqueta(alvo)
  const criar = useCriarEtiqueta()
  const [recusa, setRecusa] = useState<string | null>(null)
  const [criando, setCriando] = useState(false)
  const [nome, setNome] = useState('')
  const [cor, setCor] = useState<CorDaEtiqueta>('salvia')
  const seletor = useRef<HTMLSelectElement>(null)
  const prefixo = useId()

  /* --- O painel do modo compacto (13.15) --- */
  const [aberto, setAberto] = useState(false)
  const botao = useRef<HTMLButtonElement>(null)
  const painel = useRef<HTMLDivElement>(null)
  const idDoPainel = `${prefixo}-painel`
  /* Fechar zera a criação em curso; a recusa fica (ela passa à linha, fora do portal). */
  const fecharOPainel = (devolverOFoco: boolean) => {
    setAberto(false)
    setCriando(false)
    setNome('')
    if (devolverOFoco) {
      botao.current?.focus()
    }
  }

  /*
   * O que a tela mostra é o que o serviço devolveu por último, e não o que o *prop* trazia: a lista
   * relida chega depois, e entre os dois o chip piscaria. O *prop* volta a valer quando muda.
   */
  const [locais, setLocais] = useState<readonly string[] | null>(null)
  const [deAntes, setDeAntes] = useState(etiquetaIds)
  if (deAntes !== etiquetaIds) {
    setDeAntes(etiquetaIds)
    setLocais(null)
  }
  const ids = locais ?? etiquetaIds

  const ordem = consulta.data ?? []
  const aplicadas = resolver(ids, catalogo, ordem)
  const disponiveis = ordem.filter((etiqueta) => !ids.includes(etiqueta.id))
  const ocupado = aplicar.isPending || retirar.isPending || criar.isPending
  /*
   * O foco vai ao seletor **depois** que a retirada assenta: durante ela o seletor está
   * desabilitado, e `focus()` num controle desabilitado não faz nada (o foco cairia no `body`, com
   * o botão "×" que o tinha fora da árvore).
   */
  const focarNoSeletor = useRef(false)
  useEffect(() => {
    if (focarNoSeletor.current && !ocupado) {
      focarNoSeletor.current = false
      seletor.current?.focus()
    }
  }, [ocupado, locais])

  /* O painel aberto acompanha o botão (`usePosicaoDoPainel`); se o botão sai da parte visível, o
     painel fecha — sem levar o foco a lugar nenhum: quem rolou está lendo outra coisa. */
  const posicao = usePosicaoDoPainel(aberto, botao, painel, {
    aoSairDaJanela: () => {
      fecharOPainel(false)
    },
  })

  /* Aberto, o foco vai para dentro: ao seletor de aplicar (o próximo passo de quem abriu). Só
     depois de posicionado — antes disso o painel está `visibility: hidden`, e o navegador recusa
     o foco a um elemento invisível. Uma vez por abertura. */
  const focouAoAbrir = useRef(false)
  useEffect(() => {
    if (!aberto) {
      focouAoAbrir.current = false
      return
    }
    if (posicao === null || focouAoAbrir.current) {
      return
    }
    focouAoAbrir.current = true
    ;(seletor.current ?? painel.current)?.focus()
  }, [aberto, posicao])

  /* O clique fora fecha, e o foco volta ao botão — a não ser que o clique o tenha dado a outro
     controle (a busca, outro "+ Etiqueta"): tomá-lo de volta atrapalharia quem já seguiu adiante.
     Clicar numa célula dá o foco à área rolável da tabela (`tabindex=0`), que não é controle. */
  useEffect(() => {
    if (!aberto) {
      return
    }
    const dentro = (alvoDoEvento: EventTarget | null) =>
      alvoDoEvento instanceof Node &&
      (painel.current?.contains(alvoDoEvento) === true ||
        botao.current?.contains(alvoDoEvento) === true)
    /* O clique que **começou** dentro (selecionar o nome, arrastar e soltar fora) não é clique
       fora: o `click` chega ao ancestral comum, mas quem apertou estava no painel. */
    let comecouDentro = false
    const aoApertar = (evento: MouseEvent) => {
      comecouDentro = dentro(evento.target)
    }
    const aoClicar = (evento: MouseEvent) => {
      const comecou = comecouDentro
      comecouDentro = false
      if (comecou || dentro(evento.target)) {
        return
      }
      const ativo = document.activeElement
      const emOutroControle =
        ativo !== null &&
        painel.current?.contains(ativo) !== true &&
        ativo.matches(CONTROLES_QUE_GUARDAM_O_FOCO)
      fecharOPainel(!emOutroControle)
    }
    document.addEventListener('mousedown', aoApertar, true)
    document.addEventListener('click', aoClicar, true)
    return () => {
      document.removeEventListener('mousedown', aoApertar, true)
      document.removeEventListener('click', aoClicar, true)
    }
  }, [aberto])

  /* O componente saiu da árvore com o painel aberto: quem monta decide se a linha saiu da lista (e
     leva o foco) ou se foi só a troca de largura ou a saída da tela (13.15, H). */
  const abertoAgora = useRef(false)
  const aoSumir = useRef(aoSumirAberto)
  useEffect(() => {
    abertoAgora.current = aberto
    aoSumir.current = aoSumirAberto
  })
  useEffect(
    () => () => {
      if (abertoAgora.current) {
        aoSumir.current?.()
      }
    },
    [],
  )

  /* Aplicar, criar e retirar desabilitam o seletor (e o "×" sai da árvore): assentado o pedido, com
     sucesso ou recusa, o foco volta ao seletor — e não cai no `body` com o painel aberto. */
  async function aplicarA(etiquetaId: string) {
    setRecusa(null)
    focarNoSeletor.current = true
    try {
      setLocais(await aplicar.mutateAsync(etiquetaId))
    } catch (falha) {
      setRecusa(mensagemDe(falha))
    }
  }

  async function retirarA(etiqueta: Etiqueta) {
    setRecusa(null)
    focarNoSeletor.current = true
    try {
      setLocais(await retirar.mutateAsync(etiqueta.id))
    } catch (falha) {
      setRecusa(mensagemDe(falha))
    }
  }

  async function criarEAplicar() {
    // O `Enter` não respeita o botão desabilitado: a guarda é daqui.
    if (ocupado || nome.trim() === '') {
      return
    }
    setRecusa(null)
    try {
      const nova = await criar.mutateAsync({ nome: nome.trim(), cor, tipo: alvo.tipo })
      setCriando(false)
      setNome('')
      await aplicarA(nova.id)
    } catch (falha) {
      setRecusa(mensagemDe(falha))
    }
  }

  const catalogoFalhou = consulta.isError && ids.length > 0
  /* O catálogo falhou com o painel aberto: o painel fecha (e os ouvintes dele saem com ele). */
  if (catalogoFalhou && aberto) {
    setAberto(false)
  }
  if (catalogoFalhou) {
    // O catálogo não veio: dizer, e não sumir com os chips como se não houvesse etiqueta.
    return (
      <span className="etiquetas__erro" role="alert">
        {mensagemDe(consulta.error)}
      </span>
    )
  }
  if (!podeEditar && aplicadas.length === 0) {
    return null
  }

  const conteudo = (
    <>
      {aplicadas.map((etiqueta) => (
        <span key={etiqueta.id} className="etiquetas__aplicada">
          <ChipDeEtiqueta etiqueta={etiqueta} />
          {podeEditar ? (
            <button
              type="button"
              className="etiquetas__retirar"
              aria-label={retirarAEtiqueta(etiqueta.nome)}
              title={retirarAEtiqueta(etiqueta.nome)}
              disabled={ocupado}
              onClick={() => void retirarA(etiqueta)}
            >
              ×
            </button>
          ) : null}
        </span>
      ))}
      {podeEditar ? (
        <select
          ref={seletor}
          className="etiquetas__selecao etiquetas__selecao--aplicar"
          aria-label={aplicarEtiquetaA(nomeDoAlvo)}
          value=""
          disabled={ocupado}
          onChange={(evento) => {
            const escolha = evento.target.value
            if (escolha === VALOR_DE_CRIAR) {
              setCriando(true)
            } else if (escolha !== '') {
              void aplicarA(escolha)
            }
          }}
        >
          <option value="">{APLICAR_ETIQUETA}</option>
          {disponiveis.map((etiqueta) => (
            <option key={etiqueta.id} value={etiqueta.id}>
              {etiqueta.nome}
            </option>
          ))}
          <option value={VALOR_DE_CRIAR}>{CRIAR_NO_SELETOR}</option>
        </select>
      ) : null}
      {criando ? (
        <span className="etiquetas__nova">
          <label className="etiquetas__campo" htmlFor={`${prefixo}-nome`}>
            <span className="etiquetas__rotulo">Nome da nova etiqueta</span>
            <input
              id={`${prefixo}-nome`}
              className="etiquetas__entrada"
              value={nome}
              maxLength={TAMANHO_DO_NOME_DA_ETIQUETA}
              // O foco vai ao campo que acabou de abrir: é o próximo passo de quem escolheu criar.
              autoFocus
              onChange={(evento) => {
                setNome(evento.target.value)
              }}
              onKeyDown={(evento) => {
                // `Enter` cria — e não envia o formulário em volta (a ficha da Empresa).
                if (evento.key === 'Enter') {
                  evento.preventDefault()
                  void criarEAplicar()
                }
              }}
            />
          </label>
          <SeletorDaCor id={`${prefixo}-cor`} rotulo="Cor da nova etiqueta" valor={cor} aoTrocar={setCor} />
          <button
            type="button"
            className="etiquetas__botao"
            disabled={ocupado}
            onClick={() => void criarEAplicar()}
          >
            {CRIAR_E_APLICAR}
          </button>
          <button
            type="button"
            className="etiquetas__botao etiquetas__botao--discreto"
            onClick={() => {
              setCriando(false)
              setNome('')
            }}
          >
            Cancelar
          </button>
        </span>
      ) : null}
      {recusa === null ? null : (
        <span className="etiquetas__erro" role="alert">
          {recusa}
        </span>
      )}
    </>
  )

  if (!compacto) {
    return (
      <div className="etiquetas__do-alvo" role="group" aria-label={etiquetasDe(nomeDoAlvo)}>
        {conteudo}
      </div>
    )
  }

  return (
    <div className="etiquetas__compacto">
      <span className="etiquetas__compacto-chips">
        <ChipsDeEtiquetas
          tipo={alvo.tipo}
          ids={aplicadas.map((etiqueta) => etiqueta.id)}
          maximo={CHIPS_NA_LINHA}
        />
      </span>
      {podeEditar ? (
        <button
          ref={botao}
          type="button"
          className="etiquetas__mais"
          aria-label={maisEtiquetaPara(nomeDoAlvo)}
          aria-haspopup="dialog"
          aria-expanded={aberto}
          aria-controls={aberto ? idDoPainel : undefined}
          onClick={() => {
            if (aberto) {
              fecharOPainel(true)
            } else {
              setAberto(true)
            }
          }}
        >
          {MAIS_ETIQUETA}
        </button>
      ) : null}
      {aberto && podeEditar
        ? createPortal(
            <div
              ref={painel}
              id={idDoPainel}
              className="etiquetas__painel"
              role="dialog"
              aria-label={etiquetasDe(nomeDoAlvo)}
              tabIndex={-1}
              // Antes de medir, o painel existe sem aparecer: a primeira posição é a calculada.
              style={posicao ?? { top: 0, left: 0, visibility: 'hidden' }}
              onKeyDown={(evento) => {
                if (evento.key === 'Escape' && !evento.defaultPrevented) {
                  evento.preventDefault()
                  fecharOPainel(true)
                  return
                }
                /* O painel mora no fim do `body`: o `Tab` que sairia dele volta ao botão, e a
                   ordem de leitura segue da linha, e não do rodapé da página. */
                if (evento.key === 'Tab' && painel.current !== null) {
                  const focaveis = Array.from(
                    painel.current.querySelectorAll<HTMLElement>(FOCAVEIS_DO_PAINEL),
                  )
                  const ativo = document.activeElement
                  const saindo =
                    focaveis.length === 0 ||
                    (evento.shiftKey
                      ? ativo === focaveis[0] || ativo === painel.current
                      : ativo === focaveis[focaveis.length - 1])
                  if (saindo) {
                    evento.preventDefault()
                    fecharOPainel(true)
                  }
                }
              }}
            >
              <div className="etiquetas__do-alvo">{conteudo}</div>
            </div>,
            document.body,
          )
        : null}
      {/* A recusa de aplicar ou retirar continua à vista na linha, fora do portal, depois que o
          painel fecha. */}
      {aberto || recusa === null ? null : (
        <span className="etiquetas__erro etiquetas__erro--linha" role="alert">
          {recusa}
        </span>
      )}
    </div>
  )
}

/* --- A escolha da ficha nova (13.18) --------------------------------------------------------- */

/** O nome do grupo de campos da ficha nova. */
export const ETIQUETAS_DA_NOVA_EMPRESA = 'Etiquetas da nova empresa'
/** A escolhida que sumiu do catálogo saiu da escolha. */
export const ETIQUETA_ESCOLHIDA_REMOVIDA =
  'Uma etiqueta escolhida não existe mais e saiu da escolha.'
/** O teto da ficha nova: o "Criar e aplicar" fica desabilitado, e a tela diz por quê. */
export const limiteDeEtiquetasNaFichaNova = (limite: number) =>
  `A ficha nova leva até ${String(limite)} etiquetas.`

/**
 * As etiquetas **de Empresa** da ficha nova (13.18, chamado de 26/10, E): a Empresa ainda não
 * existe, então nada é aplicado aqui — a escolha só junta os ids, e o `POST /empresas` grava a
 * Empresa com eles na mesma transação. "Criar etiqueta…" cria no catálogo na hora e já a escolhe; se
 * o cadastro da Empresa for recusado depois, a etiqueta criada fica no catálogo (aceito pela spec).
 * Até `LIMITE_DE_ETIQUETAS_NA_FICHA_NOVA`, o teto do serviço.
 */
export function EscolhaDeEtiquetas({
  selecionadas,
  aoTrocar,
  desabilitada = false,
}: {
  selecionadas: readonly string[]
  aoTrocar: (etiquetaIds: string[]) => void
  desabilitada?: boolean
}) {
  const consulta = useEtiquetas('empresa')
  const catalogo = useCatalogoDeEtiquetas('empresa')
  const criar = useCriarEtiqueta()
  const [recusa, setRecusa] = useState<string | null>(null)
  const [criando, setCriando] = useState(false)
  const [nome, setNome] = useState('')
  const [cor, setCor] = useState<CorDaEtiqueta>('salvia')
  const prefixo = useId()

  const ordem = consulta.data ?? []
  const escolhidas = resolver(selecionadas, catalogo, ordem)
  const disponiveis = ordem.filter((etiqueta) => !selecionadas.includes(etiqueta.id))
  /* O teto conta o que a tela mostra: um id que o catálogo não tem mais não ocupa lugar. */
  const cheia = escolhidas.length >= LIMITE_DE_ETIQUETAS_NA_FICHA_NOVA
  const ocupado = desabilitada || criar.isPending
  const [podadas, setPodadas] = useState(false)

  /*
   * A escolhida que sumiu do catálogo (outra pessoa a apagou, com esta ficha aberta) sai da
   * escolha: senão ela iria no `POST` sem chip na tela, e a ficha inteira seria recusada (`422`)
   * por algo que quem cadastra não vê. A poda avisa.
   */
  const existentes = consulta.isSuccess
    ? selecionadas.filter((id) => catalogo.has(id))
    : selecionadas
  const faltando = existentes.length !== selecionadas.length
  if (faltando && !podadas) {
    setPodadas(true)
  }
  useEffect(() => {
    if (faltando) {
      aoTrocar([...existentes])
    }
  }, [faltando, existentes, aoTrocar])

  function escolher(etiquetaId: string) {
    if (selecionadas.includes(etiquetaId) || cheia) {
      return
    }
    setPodadas(false)
    aoTrocar([...selecionadas, etiquetaId])
  }

  async function criarEEscolher() {
    // O `Enter` não respeita o botão desabilitado: a guarda é daqui.
    if (ocupado || cheia || nome.trim() === '') {
      return
    }
    setRecusa(null)
    try {
      const nova = await criar.mutateAsync({ nome: nome.trim(), cor, tipo: 'empresa' })
      setCriando(false)
      setNome('')
      escolher(nova.id)
    } catch (falha) {
      setRecusa(mensagemDe(falha))
    }
  }

  return (
    <div className="etiquetas__do-alvo" role="group" aria-label={ETIQUETAS_DA_NOVA_EMPRESA}>
      {escolhidas.map((etiqueta) => (
        <span key={etiqueta.id} className="etiquetas__aplicada">
          <ChipDeEtiqueta etiqueta={etiqueta} />
          <button
            type="button"
            className="etiquetas__retirar"
            aria-label={retirarAEtiqueta(etiqueta.nome)}
            title={retirarAEtiqueta(etiqueta.nome)}
            disabled={ocupado}
            onClick={() => {
              aoTrocar(selecionadas.filter((id) => id !== etiqueta.id))
            }}
          >
            ×
          </button>
        </span>
      ))}
      <select
        className="etiquetas__selecao etiquetas__selecao--aplicar"
        aria-label={aplicarEtiquetaA('nova empresa')}
        value=""
        disabled={ocupado || cheia}
        onChange={(evento) => {
          const escolha = evento.target.value
          if (escolha === VALOR_DE_CRIAR) {
            setCriando(true)
          } else if (escolha !== '') {
            escolher(escolha)
          }
        }}
      >
        <option value="">{APLICAR_ETIQUETA}</option>
        {disponiveis.map((etiqueta) => (
          <option key={etiqueta.id} value={etiqueta.id}>
            {etiqueta.nome}
          </option>
        ))}
        <option value={VALOR_DE_CRIAR}>{CRIAR_NO_SELETOR}</option>
      </select>
      {criando ? (
        <span className="etiquetas__nova">
          <label className="etiquetas__campo" htmlFor={`${prefixo}-nome`}>
            <span className="etiquetas__rotulo">Nome da nova etiqueta</span>
            <input
              id={`${prefixo}-nome`}
              className="etiquetas__entrada"
              value={nome}
              maxLength={TAMANHO_DO_NOME_DA_ETIQUETA}
              autoFocus
              onChange={(evento) => {
                setNome(evento.target.value)
              }}
              onKeyDown={aoApertar(() => void criarEEscolher())}
            />
          </label>
          <SeletorDaCor
            id={`${prefixo}-cor`}
            rotulo="Cor da nova etiqueta"
            valor={cor}
            aoTrocar={setCor}
          />
          <button
            type="button"
            className="etiquetas__botao"
            disabled={ocupado || cheia}
            onClick={() => void criarEEscolher()}
          >
            {CRIAR_E_APLICAR}
          </button>
          <button
            type="button"
            className="etiquetas__botao etiquetas__botao--discreto"
            onClick={() => {
              setCriando(false)
              setNome('')
            }}
          >
            Cancelar
          </button>
        </span>
      ) : null}
      {cheia ? (
        <span className="etiquetas__aviso" role="status">
          {limiteDeEtiquetasNaFichaNova(LIMITE_DE_ETIQUETAS_NA_FICHA_NOVA)}
        </span>
      ) : null}
      {podadas ? (
        <span className="etiquetas__aviso" role="status">
          {ETIQUETA_ESCOLHIDA_REMOVIDA}
        </span>
      ) : null}
      {consulta.isError ? (
        <span className="etiquetas__erro" role="alert">
          {mensagemDe(consulta.error)}
        </span>
      ) : null}
      {recusa === null ? null : (
        <span className="etiquetas__erro" role="alert">
          {recusa}
        </span>
      )}
    </div>
  )
}

/* --- O gerenciador ---------------------------------------------------------------------------- */

function LinhaDoGerenciador({
  etiqueta,
  aoApagada,
  aoEmUso,
  aoFalhar,
  aoSalva,
}: {
  etiqueta: Etiqueta
  aoApagada: (etiqueta: Etiqueta) => void
  aoEmUso: (etiqueta: Etiqueta, uso: UsoDaEtiqueta, mensagem: string) => void
  aoFalhar: (mensagem: string) => void
  aoSalva: () => void
}) {
  const editar = useEditarEtiqueta()
  const apagar = useApagarEtiqueta()
  const [editando, setEditando] = useState(false)
  const [nome, setNome] = useState(etiqueta.nome)
  const [cor, setCor] = useState<CorDaEtiqueta>(etiqueta.cor)
  const prefixo = useId()

  async function salvar() {
    if (editar.isPending || nome.trim() === '') {
      return
    }
    try {
      await editar.mutateAsync({ etiquetaId: etiqueta.id, corpo: { nome: nome.trim(), cor } })
      setEditando(false)
      aoSalva()
    } catch (falha) {
      aoFalhar(mensagemDe(falha))
    }
  }

  async function apagarA() {
    try {
      await apagar.mutateAsync(etiqueta.id)
      aoApagada(etiqueta)
    } catch (falha) {
      const uso = usoDaEtiquetaEmUso(falha)
      if (uso === undefined) {
        aoFalhar(mensagemDe(falha))
      } else {
        aoEmUso(etiqueta, uso, mensagemDe(falha))
      }
    }
  }

  if (editando) {
    return (
      <li className="etiquetas__linha">
        <label className="etiquetas__campo" htmlFor={`${prefixo}-nome`}>
          <span className="etiquetas__rotulo etiquetas__so-para-leitor">{`Nome de ${etiqueta.nome}`}</span>
          <input
            id={`${prefixo}-nome`}
            className="etiquetas__entrada"
            value={nome}
            maxLength={TAMANHO_DO_NOME_DA_ETIQUETA}
            onChange={(evento) => {
              setNome(evento.target.value)
            }}
            onKeyDown={aoApertar(() => void salvar())}
          />
        </label>
        <SeletorDaCor
          id={`${prefixo}-cor`}
          rotulo={`Cor de ${etiqueta.nome}`}
          valor={cor}
          aoTrocar={setCor}
        />
        <button
          type="button"
          className="etiquetas__botao"
          disabled={editar.isPending}
          onClick={() => void salvar()}
        >
          Salvar
        </button>
        <button
          type="button"
          className="etiquetas__botao etiquetas__botao--discreto"
          onClick={() => {
            setEditando(false)
            setNome(etiqueta.nome)
            setCor(etiqueta.cor)
          }}
        >
          Cancelar
        </button>
      </li>
    )
  }
  return (
    <li className="etiquetas__linha">
      <ChipDeEtiqueta etiqueta={etiqueta} />
      <span className="etiquetas__nome-da-cor">{NOMES_DAS_CORES[etiqueta.cor]}</span>
      <button
        type="button"
        className="etiquetas__botao etiquetas__botao--discreto"
        aria-label={`Editar a etiqueta ${etiqueta.nome}`}
        onClick={() => {
          setNome(etiqueta.nome)
          setCor(etiqueta.cor)
          setEditando(true)
        }}
      >
        Editar
      </button>
      <button
        type="button"
        className="etiquetas__botao etiquetas__botao--discreto"
        aria-label={`${APAGAR_A_ETIQUETA} ${etiqueta.nome}`}
        disabled={apagar.isPending}
        onClick={() => void apagarA()}
      >
        Apagar
      </button>
    </li>
  )
}

type Desfecho =
  | { tipo: 'status'; texto: string }
  | { tipo: 'alert'; texto: string }
  | { tipo: 'em_uso'; texto: string; etiquetaId: string; uso: UsoDaEtiqueta }
  | null

export interface PropsDoGerenciador {
  /** O catálogo que esta tela gerencia (13.18): Empresas, `empresa`; Pessoas, `pessoa`. */
  tipo: TipoDeEtiqueta
  /** A etiqueta do filtro da URL — apagá-la tira o filtro (`aoApagarAEtiquetaDoFiltro`). */
  etiquetaDoFiltro?: string
  aoApagarAEtiquetaDoFiltro?: () => void
  /** O `id` da região — o `aria-controls` do botão "Etiquetas" aponta para ele (13.15). */
  id?: string
  /** `Esc` dentro do gerenciador: quem monta o fecha e devolve o foco ao botão "Etiquetas". */
  aoFechar?: () => void
}

/**
 * O gerenciador abre **acima da barra** de busca (13.15, chamado de 26/10, A), e o foco vai para
 * dentro dele (ao nome da etiqueta nova): quem apertou "Etiquetas" não precisa caçá-lo com o `Tab`.
 */
export function GerenciadorDeEtiquetas({
  tipo,
  etiquetaDoFiltro,
  aoApagarAEtiquetaDoFiltro,
  id,
  aoFechar,
}: PropsDoGerenciador) {
  const consulta = useEtiquetas(tipo)
  const criar = useCriarEtiqueta()
  const [nome, setNome] = useState('')
  const [cor, setCor] = useState<CorDaEtiqueta>('salvia')
  const [desfecho, setDesfecho] = useState<Desfecho>(null)
  const prefixo = useId()
  const campoDoNome = useRef<HTMLInputElement>(null)
  useEffect(() => {
    campoDoNome.current?.focus()
  }, [])

  async function criarA() {
    if (criar.isPending || nome.trim() === '') {
      return
    }
    setDesfecho(null)
    try {
      await criar.mutateAsync({ nome: nome.trim(), cor, tipo })
      setNome('')
      setDesfecho({ tipo: 'status', texto: ETIQUETA_CRIADA })
    } catch (falha) {
      setDesfecho({ tipo: 'alert', texto: mensagemDe(falha) })
    }
  }

  return (
    <section
      className="etiquetas__gerenciador"
      aria-label={NOME_DO_GERENCIADOR}
      id={id}
      onKeyDown={(evento) => {
        if (evento.key === 'Escape' && aoFechar !== undefined && !evento.defaultPrevented) {
          evento.preventDefault()
          aoFechar()
        }
      }}
    >
      <div className="etiquetas__nova">
        <label className="etiquetas__campo" htmlFor={`${prefixo}-nome`}>
          <span className="etiquetas__rotulo">Nome da etiqueta</span>
          <input
            ref={campoDoNome}
            id={`${prefixo}-nome`}
            className="etiquetas__entrada"
            value={nome}
            maxLength={TAMANHO_DO_NOME_DA_ETIQUETA}
            onChange={(evento) => {
              setNome(evento.target.value)
            }}
            onKeyDown={aoApertar(() => void criarA())}
          />
        </label>
        <SeletorDaCor id={`${prefixo}-cor`} rotulo="Cor da etiqueta" valor={cor} aoTrocar={setCor} />
        <button
          type="button"
          className="etiquetas__botao"
          disabled={criar.isPending || nome.trim() === ''}
          onClick={() => void criarA()}
        >
          {CRIAR_ETIQUETA}
        </button>
      </div>

      {desfecho === null ? null : desfecho.tipo === 'status' ? (
        <p className="etiquetas__aviso" role="status">
          {desfecho.texto}
        </p>
      ) : (
        <p className="etiquetas__erro" role="alert">
          {/* Verbatim: a mensagem do servidor é a que sabe contar (FR-62, PN4). */}
          {desfecho.texto}
          {/* 13.18: um número só, e um caminho só — o da lista do tipo da etiqueta. */}
          {desfecho.tipo === 'em_uso' && desfecho.uso.quantidade > 0 ? (
            <span className="etiquetas__caminhos">
              <Link
                to={desfecho.uso.tipo === 'empresa' ? CAMINHOS.empresas : CAMINHOS.pessoas}
                search={{ etiqueta: desfecho.etiquetaId }}
                state={(anterior) => ({
                  ...anterior,
                  usoDaEtiqueta: { etiquetaId: desfecho.etiquetaId, ...desfecho.uso },
                })}
                className="etiquetas__caminho"
              >
                {desfecho.uso.tipo === 'empresa' ? VER_EMPRESAS : VER_PESSOAS}
              </Link>
            </span>
          ) : null}
        </p>
      )}

      {consulta.isError ? (
        <p className="etiquetas__erro" role="alert">
          {mensagemDe(consulta.error)}
        </p>
      ) : (consulta.data ?? []).length === 0 && consulta.isSuccess ? (
        <p className="etiquetas__aviso">{SEM_ETIQUETAS}</p>
      ) : (
        <ul className="etiquetas__lista">
          {(consulta.data ?? []).map((etiqueta) => (
            <LinhaDoGerenciador
              key={etiqueta.id}
              etiqueta={etiqueta}
              aoSalva={() => {
                setDesfecho({ tipo: 'status', texto: ETIQUETA_SALVA })
              }}
              aoFalhar={(texto) => {
                setDesfecho({ tipo: 'alert', texto })
              }}
              aoApagada={(apagada) => {
                setDesfecho({ tipo: 'status', texto: ETIQUETA_APAGADA })
                if (apagada.id === etiquetaDoFiltro) {
                  aoApagarAEtiquetaDoFiltro?.()
                }
              }}
              aoEmUso={(emUso, uso, texto) => {
                setDesfecho({ tipo: 'em_uso', texto, etiquetaId: emUso.id, uso })
              }}
            />
          ))}
        </ul>
      )}
    </section>
  )
}

/**
 * O botão "Etiquetas" da barra das listas — abre e fecha o gerenciador. `controla` é o `id` dele, e
 * o `aria-controls` só existe com ele aberto: fechado, o `id` não está na página.
 */
export function BotaoDeEtiquetas({
  aberto,
  aoTrocar,
  controla,
  refDoBotao,
}: {
  aberto: boolean
  aoTrocar: () => void
  controla?: string
  refDoBotao?: Ref<HTMLButtonElement>
}) {
  return (
    <button
      ref={refDoBotao}
      type="button"
      className="empresas__botao empresas__botao--discreto"
      aria-expanded={aberto}
      aria-controls={aberto ? controla : undefined}
      onClick={aoTrocar}
    >
      {ROTULO_DO_BOTAO_DE_ETIQUETAS}
    </button>
  )
}

/* --- O filtro --------------------------------------------------------------------------------- */

/** O que a lista diz no lugar das linhas quando a etiqueta da URL não está no catálogo (13.18). */
export const FILTRO_SEM_A_ETIQUETA =
  'A etiqueta deste filtro não existe mais nesta lista. Use “Limpar filtro” para ver todas.'

/**
 * A etiqueta do filtro da URL diante do catálogo do tipo da tela (13.18): `pronta` diz se a lista
 * pode perguntar, e `removida` que o catálogo carregado não a tem — apagada, ou de outro tipo (o
 * link antigo de uma etiqueta que era mista). Removida, a lista **não** é chamada: o serviço daria o
 * `422` do outro tipo, ou a lista vazia de uma etiqueta que não existe. Com o catálogo em falha, a
 * lista pergunta assim mesmo (a recusa do serviço é a que sobra).
 */
export function useEtiquetaDoFiltro(
  tipo: TipoDeEtiqueta,
  etiquetaId: string | undefined,
): { pronta: boolean; removida: boolean } {
  const consulta = useEtiquetas(tipo)
  if (etiquetaId === undefined || consulta.isError) {
    return { pronta: true, removida: false }
  }
  if (!consulta.isSuccess) {
    return { pronta: false, removida: false }
  }
  const removida = !consulta.data.some((etiqueta) => etiqueta.id === etiquetaId)
  return { pronta: !removida, removida }
}

export function FiltroPorEtiqueta({
  tipo,
  valor,
  aoTrocar,
}: {
  /** O catálogo da lista (13.18): o filtro só oferece as etiquetas do tipo dela. */
  tipo: TipoDeEtiqueta
  /** A etiqueta da URL, ou `undefined`. */
  valor: string | undefined
  aoTrocar: (etiquetaId: string | undefined) => void
}) {
  const consulta = useEtiquetas(tipo)
  const id = useId()
  const etiquetas = consulta.data ?? []
  const removida =
    valor !== undefined && consulta.isSuccess && !etiquetas.some((etiqueta) => etiqueta.id === valor)
  return (
    <span className="etiquetas__filtro">
      <label className="etiquetas__rotulo etiquetas__so-para-leitor" htmlFor={id}>
        {FILTRAR_POR_ETIQUETA}
      </label>
      <select
        id={id}
        className="etiquetas__selecao"
        value={valor ?? ''}
        onChange={(evento) => {
          aoTrocar(evento.target.value === '' ? undefined : evento.target.value)
        }}
      >
        <option value="">{TODAS_AS_ETIQUETAS}</option>
        {etiquetas.map((etiqueta) => (
          <option key={etiqueta.id} value={etiqueta.id}>
            {etiqueta.nome}
          </option>
        ))}
        {removida ? <option value={valor}>{ETIQUETA_REMOVIDA}</option> : null}
      </select>
      {removida ? (
        <button
          type="button"
          className="empresas__botao empresas__botao--discreto"
          onClick={() => {
            aoTrocar(undefined)
          }}
        >
          {LIMPAR_FILTRO}
        </button>
      ) : null}
    </span>
  )
}

/* --- O aviso das escondidas ------------------------------------------------------------------- */

/**
 * O aviso das linhas que quem olha não enxerga (D-11), ou `undefined`.
 *
 * Só existe com o *state* do aviso de apagar **para a etiqueta da URL**: a lista compara, uma vez,
 * o total do `409` com o total que ela enxerga (o `total` da resposta, e nunca as linhas da página),
 * na primeira resposta assentada **sem busca** — a busca encolheria o total por outro motivo. O
 * número fica guardado: retirar as visíveis não muda quantas estão escondidas.
 */
export function useAvisoDasEscondidas({
  tipo,
  etiquetaId,
  busca,
  totalVisivel,
}: {
  tipo: 'empresas' | 'pessoas'
  etiquetaId: string | undefined
  busca: string
  /** O total do filtro na resposta assentada, ou `undefined` enquanto ela não chega. */
  totalVisivel: number | undefined
}): string | undefined {
  const roteador = useRouter()
  const localizacao = useRouterState({ select: (estado) => estado.location })
  const uso = localizacao.state.usoDaEtiqueta
  /* 13.18: o número é do tipo da etiqueta — a lista do outro tipo não tem o que comparar. */
  const daEtiqueta =
    uso !== undefined &&
    etiquetaId !== undefined &&
    uso.etiquetaId === etiquetaId &&
    typeof uso.quantidade === 'number' &&
    (uso.tipo === 'empresa' ? 'empresas' : 'pessoas') === tipo
  const guardadas = daEtiqueta ? uso.escondidas : undefined

  useEffect(() => {
    if (
      !daEtiqueta ||
      guardadas !== undefined ||
      busca.trim() !== '' ||
      totalVisivel === undefined
    ) {
      return
    }
    roteador.history.replace(localizacao.href, {
      ...localizacao.state,
      usoDaEtiqueta: { ...uso, escondidas: Math.max(0, uso.quantidade - totalVisivel) },
    })
  }, [daEtiqueta, guardadas, busca, totalVisivel, roteador, localizacao, uso, tipo])

  /* Com busca, o total encolhe por outro motivo: o aviso cala enquanto ela estiver lá. */
  if (guardadas === undefined || guardadas === 0 || busca.trim() !== '') {
    return undefined
  }
  return avisoDasEscondidas(tipo, guardadas)
}
